import { useState, useEffect, useRef } from "react";
import Button from "../../Button";
import apiInstance from "../../apiInstance";
import defaultDocImg from "../../../assets/images/doc.png";

const EditTicketPopup = ({
    data,
    type = "add",
    updateTicket,
    addTicket,
    updateError,
    closePopup,
}) => {
    const [ticket, setTicket] = useState({
        id: data?.ticket?._id || "",
        user: data?.ticket?.user?._id || '',
        subject: data?.ticket?.subject || '',
        description: data?.ticket?.description || '',
        status: data?.ticket?.status || '',
        category: data?.ticket?.category || '',
        priority: data?.ticket?.priority || '',
    });

    const [existingAttachments, setExistingAttachments] = useState(
        data?.ticket?.attachments || []
    );
    const [newFiles, setNewFiles] = useState([]);
    const [users, setUsers] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const fileInputRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTicket((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        const totalCount = existingAttachments.length + newFiles.length + selected.length;
        if (totalCount > 5) {
            updateError("Maximum 5 attachments allowed.");
            return;
        }
        setNewFiles((prev) => [...prev, ...selected]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeNewFile = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const removeExistingAttachment = (index) => {
        setExistingAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        const formData = new FormData();
        formData.append("user", ticket.user);
        formData.append("subject", ticket.subject);
        formData.append("description", ticket.description);
        formData.append("status", ticket.status);
        formData.append("category", ticket.category);
        formData.append("priority", ticket.priority);
        formData.append("existingAttachments", JSON.stringify(existingAttachments));

        newFiles.forEach((file) => {
            formData.append("attachments", file);
        });

        const apiCall =
            type === "update"
                ? apiInstance.put(`/devadmin/support-ticket/${ticket.id}`, formData)
                : apiInstance.post(`/devadmin/support-ticket`, formData);

        apiCall
            .then((response) => {
                const returnedTicket = response.data.ticket;
                if (type === "update") {
                    updateTicket(returnedTicket);
                } else {
                    addTicket(returnedTicket);
                }
                closePopup();
            })
            .catch((error) => {
                console.log(error)
                if (error.response) {
                    if (
                        error.response.status === 400 &&
                        error.response.data.fieldErrors
                    ) {
                        setFieldErrors(error.response.data.fieldErrors);
                    } else {
                        updateError(error.response.data.message || 'Something went wrong, please try again later.');
                    }
                }
            });
    };

    useEffect(() => {
        apiInstance
            .get("/devadmin/user")
            .then((res) => {
                setUsers(res.data.users || []);
            })
            .catch((err) => {
                console.error("Error fetching users:", err);
                updateError("Failed to load users.");
            });
    }, []);

    return (
        <div className="mjsdev-pp">
            <h2>{type === "add" ? "ADD SUPPORT TICKET" : "EDIT SUPPORT TICKET"}</h2>

            <form>

                <div className="mjsdev-input-field">
                    <label htmlFor="user">Customer</label>
                    <select
                        name="user"
                        value={ticket.user}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Customer</option>
                        {users.map((user) => (
                            <option key={user._id} value={user._id}>
                                {user.email}
                            </option>
                        ))}

                    </select>
                    {fieldErrors.user && (
                        <span className="mjsdev-error">
                            {fieldErrors.user}
                        </span>
                    )}
                </div>

                <div className="mjsdev-input-field">
                    <label htmlFor="subject">Subject</label>
                    <input
                        type="text"
                        placeholder="Enter subject"
                        name="subject"
                        value={ticket.subject}
                        onChange={handleInputChange}
                    />
                    {fieldErrors.subject && (
                        <span className="mjsdev-error">
                            {fieldErrors.subject}
                        </span>
                    )}
                </div>

                <div className="mjsdev-input-field">
                    <label htmlFor="status">Status</label>
                    <select
                        name="status"
                        value={ticket.status}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Status</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>

                    </select>
                    {fieldErrors.status && (
                        <span className="mjsdev-error">
                            {fieldErrors.status}
                        </span>
                    )}
                </div>

                <div className="mjsdev-input-field">
                    <label htmlFor="category">Category</label>
                    <select
                        name="category"
                        value={ticket.category}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Category</option>
                        <option value="billing">Billing</option>
                        <option value="technical">Technical</option>
                        <option value="account">Account</option>
                        <option value="website">Website</option>
                        <option value="feature_request">Feature Request</option>
                        <option value="new_website_request">Website Request</option>
                        <option value="other">Other</option>

                    </select>
                    {fieldErrors.category && (
                        <span className="mjsdev-error">
                            {fieldErrors.category}
                        </span>
                    )}
                </div>

                <div className="mjsdev-input-field">
                    <label htmlFor="priority">Priority</label>
                    <select
                        name="priority"
                        value={ticket.priority}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Priority</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>

                    </select>
                    {fieldErrors.priority && (
                        <span className="mjsdev-error">
                            {fieldErrors.priority}
                        </span>
                    )}
                </div>

                <div className="mjsdev-input-field">
                    <label htmlFor="description">Note</label>
                    <textarea
                        name="description"
                        value={ticket.description}
                        onChange={handleInputChange}
                    >
                        {ticket.description}
                    </textarea>
                </div>

                <div className="mjsdev-input-field">
                    <label>Attachments</label>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.doc,.docx"
                        onChange={handleFileChange}
                    />
                    <small>Allowed: Images (jpg, png, gif, webp) & Documents (pdf, doc, docx). Max 10MB each, up to 5 files.</small>

                    {existingAttachments.length > 0 && (
                        <div className="mjsdev-attachment-list">
                            {existingAttachments.map((att, i) => (
                                <div key={att.filename} className="mjsdev-attachment-item">
                                    {att.mimetype?.startsWith("image/") ? (
                                        <img
                                            src={`${process.env.REACT_APP_SERVER_URL}/uploads/support-tickets/${att.filename}`}
                                            alt={att.originalName}
                                            className="mjsdev-attachment-thumb"
                                        />
                                    ) : (
                                        <img
                                            src={defaultDocImg}
                                            alt={att.originalName}
                                            className="mjsdev-attachment-thumb"
                                        />
                                    )}
                                    <span>{att.originalName}</span>
                                    <button type="button" onClick={() => removeExistingAttachment(i)}>&times;</button>
                                </div>
                            ))}
                        </div>
                    )}

                    {newFiles.length > 0 && (
                        <div className="mjsdev-attachment-list">
                            {newFiles.map((file, i) => (
                                <div key={i} className="mjsdev-attachment-item">
                                    {file.type?.startsWith("image/") ? (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            className="mjsdev-attachment-thumb"
                                        />
                                    ) : (
                                        <img
                                            src={defaultDocImg}
                                            alt={file.name}
                                            className="mjsdev-attachment-thumb"
                                        />
                                    )}
                                    <span>{file.name}</span>
                                    <button type="button" onClick={() => removeNewFile(i)}>&times;</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </form>

            <div className="mjsdev-pp-actions">
                <Button
                    name={type === "add" ? "Add" : "Update"}
                    eventHandler={handleSubmit}
                />
                <Button name="Cancel" eventHandler={closePopup} />
            </div>
            
        </div>
    );
};

export default EditTicketPopup;
